'use client';

import { CaretDown } from 'phosphor-icons';
import { useState } from 'react';
import { c } from 'ui-components';
import { FacetAccordionProps } from './FacetAccordion.types';

type Variant = 'sidebar' | 'drawer' | 'mobile';

const wrapperStyles: Record<Variant, string> = {
  sidebar: '',
  drawer: 'border-b border-grey-500 dark:border-line-strong',
  mobile: 'border-b border-grey-800',
};

const buttonStyles: Record<Variant, string> = {
  sidebar: 'flex w-full items-center justify-between text-left py-2',
  drawer: 'flex w-full items-center justify-between text-left h-10 px-3',
  mobile: 'flex w-full items-center justify-between text-left h-14 px-4',
};

const titleStyles: Record<Variant, string> = {
  sidebar: 'text-base font-normal text-content tracking-[-0.096px]',
  drawer: 'text-sm text-content-muted',
  mobile: 'text-sm font-medium uppercase tracking-wider text-grey-300',
};

const caretStyles: Record<Variant, string> = {
  sidebar: 'shrink-0 transition-transform duration-200 text-content-subtle',
  drawer:
    'shrink-0 transition-transform duration-200 text-grey-500 dark:text-content-subtle',
  mobile: 'shrink-0 transition-transform duration-200 text-grey-500',
};

const contentStyles: Record<Variant, string> = {
  sidebar: 'pb-2 pt-1',
  drawer: 'px-3 pb-3',
  mobile: 'px-4 pb-4',
};

export const FacetAccordion = ({
  title,
  children,
  defaultOpen = true,
  className,
  onClear,
  clearLabel = 'Clear',
  showClear = false,
  disabled = false,
  variant = 'sidebar',
  selectedCount,
  isOpen: controlledIsOpen,
  onToggle,
}: FacetAccordionProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalIsOpen((prev) => !prev);
    }
  };

  const isSidebar = variant === 'sidebar';
  const titleId = isSidebar
    ? `facet-title-${title.toLowerCase().replace(/\s+/g, '-')}`
    : undefined;

  return (
    <div
      className={c(wrapperStyles[variant], className)}
      role={isSidebar ? 'group' : undefined}
      aria-labelledby={titleId}
    >
      {isSidebar ? (
        <div className={c(buttonStyles[variant], 'px-2')}>
          <h3 id={titleId} className={c(titleStyles[variant], 'm-0')}>
            {title}
          </h3>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          className={buttonStyles[variant]}
          aria-expanded={isOpen}
        >
          <span className={titleStyles[variant]}>
            {title}
            {variant === 'mobile' &&
              selectedCount !== undefined &&
              selectedCount > 0 && (
                <span className="text-grey-400"> ({selectedCount})</span>
              )}
          </span>
          <CaretDown
            size={16}
            className={c(caretStyles[variant], {
              'rotate-180': isOpen,
            })}
          />
        </button>
      )}
      {(isSidebar || isOpen) && (
        <div className={contentStyles[variant]}>
          {children}
          {showClear && onClear && (
            <button
              type="button"
              className="mt-2 text-sm text-link hover:underline disabled:cursor-not-allowed disabled:text-grey-400 disabled:no-underline"
              onClick={onClear}
              disabled={disabled}
            >
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
